import { Dialog } from "@progress/kendo-react-dialogs";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "src/util/notify";
import { useNavigate, useLocation } from "react-router";
import { useSelector } from "react-redux";
import InputKendoRct from "../../control-components/input/input";
import Loader from "../../control-components/loader/loader";
import PhoneInputMask from "../../control-components/phone-input-mask/phone-input-mask";
import ZipCodeInput from "../../control-components/zip-code/zip-code";
import ErrorHelper from "../../helper/error-helper";
import NOTIFICATION_MESSAGE from "../../helper/notification-messages";
import { clinicService } from "../../services/clinicService";
import ApiUrls from "../../helper/api-urls";
import ApiHelper from "../../helper/api-helper";
import APP_ROUTES from "../../helper/app-routes";
import DropDownKendoRct from "../../control-components/drop-down/drop-down";
import ZipCodeOptInput from "../../control-components/zip-code/zip-code-opt";
import { Checkbox } from "@progress/kendo-react-inputs";
import DatePickerKendoRct from "../../control-components/date-picker/date-picker";

const status = [
  {
    id: 1,
    isActive: true,
    name: "Active",
  },
  {
    id: 2,
    isActive: false,
    name: "Inactive",
  },
];

function AddClinic({ onClose, selectedId }) {
  const regionData = useSelector((state) => state.getRegion);
  const statesData = useSelector((state) => state.clinicStatesReducer);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState("");
  const [settingError, setSettingError] = useState(false);
  const [domainNameAvailabe, setDomainNameAvailable] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [userSeatsPriceList, setUserSeatsPriceList] = useState([]);
  const [oneTimeSetupPriceList, setOneTimeSetupPriceList] = useState([]);

  let [fields, setFields] = useState({
    domainName: "",
    id: "",
    clinicName: "",
    address: "",
    state: "",
    city: "",
    zip: "",
    zipOpt: "",
    phone: "",
    fax: "",
    email: "",
    npi: "",
    taxId: "",
    websiteUrl: "",
    regionId: "",
    userSeats: "",
    activeStaffCount: "",
    clinicStatus: "",
    userSeatsPrice: "",
    oneTimeSetupPrice: "",
    subscriptionStartDate: "",
    isRCM: false,
    commissionPercentage: "",
    salesPerson: "",
    referral: ""
  });

  useEffect(() => {
    if (!state) {
      getStripeProduct();
    }
  }, []);

  const getStripeProduct = async () => {
    setLoading(true);
    await clinicService
      .getStripeProduct()
      .then((result) => {
        setLoading(false);
        const stripeAllProducts = result.resultData;
        if (stripeAllProducts) {
          stripeAllProducts.forEach((item) => {
            if (item?.name === "Notenetic User Seats") {
              getUserSeatsPrice(item.stripeProductId);
            } else if (item?.name === "Implementation Fees") {
              getOneTimeSetupPrice(item.stripeProductId);
            }
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        NotificationManager.error(error?.message);
      });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value / 100);
  };

  const getUserSeatsPrice = async (stripeProductId) => {
    setLoading(true);
    await clinicService
      .getUserSeatsPriceList(stripeProductId)
      .then((result) => {
        setLoading(false);
        if (result?.resultData) {
          const sortedData = result.resultData
            .sort((a, b) => a.unitAmount - b.unitAmount)
            .map((item) => ({
              ...item,
              unitAmount: item?.duration
                ? `${formatCurrency(item.unitAmount)} per ${item?.duration}`
                : formatCurrency(item.unitAmount),
            }));

          setUserSeatsPriceList(sortedData);
        }
      })
      .catch((error) => {
        setLoading(false);
        NotificationManager.error(error?.message);
      });
  };

  const getOneTimeSetupPrice = async (stripeProductId) => {
    setLoading(true);
    await clinicService
      .getOneTimeSetupPriceList(stripeProductId)
      .then((result) => {
        setLoading(false);
        if (result?.resultData) {
          const data = result.resultData.map((item) => ({
            ...item,
            unitAmount: item?.duration
              ? `${formatCurrency(item.unitAmount)} per ${item?.duration}`
              : formatCurrency(item.unitAmount),
          }));
          setOneTimeSetupPriceList(data);
        }
      })
      .catch((error) => {
        setLoading(false);
        NotificationManager.error(error?.message);
      });
  };

  function validateNPI(npi) {
    // remove any whitespace or dashes from the input string
    npi = npi.replace(/\s+|-/g, "");

    // NPI must be exactly 10 digits long
    if (npi.length !== 10) {
      return false;
    }

    // reverse the input string for easier processing
    npi = npi.split("").reverse().join("");

    let sum = 0;
    for (let i = 1; i < npi.length; i++) {
      let digit = parseInt(npi[i], 10);
      // multiply every other digit by 2, starting with the second to last
      if (i % 2 === 1) {
        digit *= 2;
      }
      // if the doubled digit is greater than 9, sum its digits (i.e., 10 becomes 1+0=1)
      if (digit > 9) {
        digit = digit
          .toString()
          .split("")
          .reduce((a, b) => parseInt(a) + parseInt(b));
      }
      sum += digit;
    }
    // the NPI is valid if the sum of its digits is divisible by 10
    const nextMultipleOfTen = Math.ceil((sum + 24) / 10) * 10;
    const calculatedCheckDigit = nextMultipleOfTen - (sum + 24);
    return (
      calculatedCheckDigit ===
      parseInt(npi.split("").reverse().join("").slice(-1))
    );
  }

  useEffect(() => {
    setIsValid(validateNPI(fields?.npi));
  }, [fields.npi]);

  useEffect(() => {
    if (state) {
      getClinicById();
    }
  }, []);

  const getDomainName = (e) => {
    ApiHelper.getRequest(ApiUrls.IS_DUPLICATE_DOMAIN_IN_CLINIC + e)
      .then((result) => {
        setDomainNameAvailable(result.resultData.isDuplicateDomain);
      })
      .catch((error) => {
        NotificationManager.error(error.message);
      });
  };

  const handleValidation = () => {
    let errors = {};
    let formIsValid = true;
    var emailPattern = new RegExp(
      /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
    );

    if (!emailPattern.test(fields.email)) {
      formIsValid = false;
      errors["email"] = ErrorHelper.INVALID_EMAIL;
    }
    if (!fields.state) {
      formIsValid = false;
      errors["state"] = ErrorHelper.FIELD_BLANK;
    }

    if (!fields.domainName || fields.domainName.trim().length === 0) {
      formIsValid = false;
      errors["domainName"] = ErrorHelper.FIELD_BLANK;
    } else if (domainNameAvailabe) {
      formIsValid = false;
      errors["domainName"] = ErrorHelper.DOMAIN_NAME;
    }

    if (!fields.clinicName || fields.clinicName.trim().length === 0) {
      formIsValid = false;
      errors["clinicName"] = ErrorHelper.FIELD_BLANK;
    }
    if (!fields.phone) {
      formIsValid = false;
      errors["phone"] = ErrorHelper.FIELD_BLANK;
    }

    if (
      !fields.userSeats ||
      isNaN(fields.userSeats) ||
      parseInt(fields.userSeats) <= 0
    ) {
      formIsValid = false;
      errors["userSeats"] = ErrorHelper.INVALID_USER_SEATS;
    }
    if (!fields.npi) {
      formIsValid = false;
      errors["npi"] = ErrorHelper.NPI;
    }

    if (!state) {
      if (!fields.userSeatsPrice) {
        formIsValid = false;
        errors["userSeatsPrice"] = ErrorHelper.FIELD_BLANK;
      }
      if (!fields.subscriptionStartDate) {
        formIsValid = false;
        errors["subscriptionStartDate"] = ErrorHelper.FIELD_BLANK;
      }
      if (!fields.regionId) {
        formIsValid = state ? true : false;
        errors["regionId"] = ErrorHelper.FIELD_BLANK;
      }
    }

    if (!isValid) {
      formIsValid = false;
    }

    setErrors(errors);
    return formIsValid;
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    if (e.target.name === "domainName") {
      setFields({
        ...fields,
        [name]: value,
      });
      if (value.length > 3) {
        getDomainName(value);
      }
    }
    setFields({
      ...fields,
      [name]: value,
    });
  };

  const handleStatusChange = (e) => {
    if (e.target.value === null) {
      setFields({
        ...fields,
        ["clinicStatus"]: status.find(
          (item) => item?.isActive === state?.isActive
        ),
      });
    } else {
      const value = e.target.value;
      setFields({
        ...fields,
        ["clinicStatus"]: value,
      });
    }
  };

  const handleValueChange = (e) => {
    const name = e.target.name;
    const rawValue = e.target.rawValue;
    setFields({
      ...fields,
      [name]: rawValue,
    });
  };

  const handleSubmit = (event) => {
    setSettingError(true);
    if (handleValidation()) {
      if (state) {
        updateClinic();
      } else {
        addClinic();
      }
    }
  };

  const addClinic = async () => {
    setLoading(true);
    await clinicService
      .addClinic(fields, sendWelcomeEmail)
      .then((result) => {
        setLoading(false);
        NotificationManager.success(NOTIFICATION_MESSAGE.CLINIC_DOMAIN_ADDED);
        navigate(APP_ROUTES.CLINIC);
      })
      .catch((error) => {
        setLoading(false);
        NotificationManager.error(error);
      });
  };
  const updateClinic = async () => {
    setLoading(true);
    await clinicService
      .updateClinic(fields, state)
      .then((result) => {
        setLoading(false);
        NotificationManager.success(NOTIFICATION_MESSAGE.CLINIC_UDDATED);
        navigate(APP_ROUTES.CLINIC);
      })
      .catch((error) => {
        setLoading(false);
        NotificationManager.error(error);
      });
  };

  const getClinicById = async () => {
    setLoading(true);
    await clinicService
      .getClinicById(state)
      .then((result) => {
        if (result.resultData) {
          let intZip = result.resultData.zip.slice(0, 5);
          let lastZip = result.resultData.zip.slice(5);
          setFields({
            ...fields,
            domainName: result.resultData.domainName,
            id: result.resultData.id,
            clinicName: result.resultData.clinicName,
            address: result.resultData.address,
            state:statesData?.find((item) => item?.shortName === result?.resultData?.state),
            city: result.resultData.city,
            zip: intZip,
            zipOpt: lastZip,
            phone: result.resultData.phone,
            fax: result.resultData.fax,
            email: result.resultData.email,
            npi: result.resultData.npi,
            taxId: result.resultData.taxId,
            websiteUrl: result.resultData.websiteUrl,
            userSeats: result.resultData.maxAllowedUserSeats,
            activeStaffCount: result.resultData.activeStaffCount,
            isRCM: result.resultData.isRCM,
            commissionPercentage: result.resultData.commissionPercentage,
            subscriptionStartDate: result.resultData.subscriptionStartDate,
            salesPerson: result.resultData.salesPerson,
            referral: result.resultData.referral,
            clinicStatus: status.find(
              (item) => item.isActive === state.isActive
            ),
          });
        }

        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        NotificationManager.error(error);
      });
  };

  const handleCancel = () => {
    navigate(APP_ROUTES.CLINIC);
  };
  const onCheckboxChange = (e) => {
    setSendWelcomeEmail(e.target.value);
  };

  function handlePriceChange(event) {
    const { name, value } = event.target;
    if (value != null) {
      setFields((prevFields) => ({
        ...prevFields,
        [name]: value,
      }));
    }
  }

  const handleCheckboxChange = (e) => {
    const { name, value } = e.target;
    setFields({
      ...fields,
      [name]: value
    });
  };

  return (
    <>
      <div className="client-accept notenetic-container placeholder-size">
        <div className="row">
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              value={fields.clinicName}
              onChange={handleChange}
              name="clinicName"
              label="Clinic Name"
              error={fields.clinicName === "" && errors.clinicName}
              validityStyles={settingError}
              required={true}
              className="form-control"
              placeholder={"Clinic Name"}
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              type="text"
              validityStyles={settingError}
              value={fields.domainName}
              onChange={handleChange}
              name="domainName"
              label="Domain Name"
              error={
                domainNameAvailabe === true
                  ? ErrorHelper.DOMAIN_NAME
                  : fields.domainName === "" && errors.domainName
              }
              required={true}
              placeholder={"Domain Name"}
              className="form-control fw-2 "
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              value={fields.address}
              onChange={handleChange}
              name="address"
              label="Address"
              className="form-control"
              placeholder="Address"
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              className="form-control"
              value={fields.city}
              onChange={handleChange}
              name="city"
              label="City"
              placeholder="City"
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12 edit-Service-popup">
            <DropDownKendoRct
              filterable={true}
              label="State"
              placeholder="State"
              maxLength={2}
              minLength={2}
              required={true}
              error={fields.state === "" && errors.state}
              validityStyles={settingError}
              onChange={handleChange}
              data={statesData}
              value={fields.state}
              textField="stateName"
              suggest={true}
              name="state"
            />
          </div>

          {/* <div className="mb-3 col-lg-4 col-md-6 col-12">
            <ZipCodeInput
              value={fields.zip}
              onChange={handleValueChange}
              name="zip"
              label="Zip Code"
              placeholder="Zip Code"
              className="form-control"
            />
          </div> */}
          <div className="mb-3 col-lg-4 col-md-6 col-1">
            <div className="zip-divide d-flex gap-2 align-items-end">
              <div className="custom-zip-divider">
                <ZipCodeInput
                  value={fields.zip}
                  onChange={handleValueChange}
                  name="zip"
                  label="Zip Code"
                  placeholder="Zip Code"
                />
              </div>
              <span
                style={{
                  fontSize: "32px",
                  color: "#bcbebf",
                }}
              >
                -
              </span>
              <ZipCodeOptInput
                value={fields.zipOpt}
                onChange={handleValueChange}
                className={"zip-dash"}
                name="zipOpt"
              />
            </div>
          </div>

          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <PhoneInputMask
              validityStyles={settingError}
              value={fields.phone}
              onChange={handleValueChange}
              name="phone"
              label="Phone"
              error={fields.phone === "" && errors.phone}
              required={true}
              placeholder="Phone"
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <PhoneInputMask
              value={fields.fax}
              onChange={handleValueChange}
              name="fax"
              label="Fax"
              placeholder="Fax"
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              className="form-control"
              value={fields.email}
              onChange={handleChange}
              name="email"
              label="Email"
              placeholder="Email"
              required={true}
              error={fields.email === "" && errors.email}
              validityStyles={settingError}
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              validityStyles={settingError}
              className="form-control"
              value={fields.npi}
              onChange={handleChange}
              name="npi"
              label="NPI"
              placeholder="NPI"
              error={!fields.npi && errors.npi}
            />
            {isValid === false && fields.npi && (
              <p
                style={{
                  color: "#d61923",
                  fontStyle: "italic",
                  fontSize: "12px",
                  marginTop: "2px",
                }}
              >
                Invalid NPI
              </p>
            )}
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              className="form-control"
              value={fields.taxId}
              onChange={handleChange}
              name="taxId"
              label="Tax ID"
              placeholder="Tax ID"
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              className="form-control"
              value={fields.websiteUrl}
              onChange={handleChange}
              name="websiteUrl"
              label="Website Url"
              placeholder="Website URL"
            />
          </div>
          {state ? (
            ""
          ) : (
            <div className="mb-3 col-lg-4 col-md-6 col-12 edit-Service-popup">
              <DropDownKendoRct
                validityStyles={settingError}
                label="Region "
                onChange={handleChange}
                data={regionData}
                value={fields.regionId}
                textField="regionName"
                suggest={true}
                name="regionId"
                error={fields.regionId === "" && errors.regionId}
                required={true}
              />
            </div>
          )}

          <div className="mb-3 col-lg-4 col-md-6 col-12 edit-Service-popup">
            <DropDownKendoRct
              filterable={true}
              label="Status"
              onChange={handleStatusChange}
              data={status}
              value={fields?.clinicStatus}
              textField="name"
              name="name"
              dataItemKey="id"
              suggest={true}
            />
          </div>

          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              className="form-control"
              value={fields.userSeats}
              onChange={handleChange}
              name="userSeats"
              label="User Seats"
              placeholder="User Seats"
              error={errors.userSeats}
              validityStyles={settingError}
              required={true}
            />
          </div>

          {!state && (
            <div className="mb-3 col-lg-4 col-md-6 col-12 edit-Service-popup">
              <DropDownKendoRct
                filterable={true}
                label="User Seats Price"
                onChange={handlePriceChange}
                data={userSeatsPriceList}
                value={fields?.userSeatsPrice}
                textField="unitAmount"
                name="userSeatsPrice"
                dataItemKey="stripePriceId"
                suggest={true}
                error={!fields.userSeatsPrice && errors.userSeatsPrice}
              />
            </div>
          )}

          {!state && (
            <>
              <div className="mb-1 col-lg-4 col-md-6 col-12 edit-Service-popup">
                <DropDownKendoRct
                  filterable={false}
                  label="Implementation fee"
                  onChange={handlePriceChange}
                  data={oneTimeSetupPriceList}
                  value={fields?.oneTimeSetupPrice}
                  textField="unitAmount"
                  name="oneTimeSetupPrice"
                  dataItemKey="stripePriceId"
                  suggest={true}
                />
              </div>

              <div className="mt-2 col-lg-4 col-md-6 col-12 date-field">
                <DatePickerKendoRct
                  value={fields?.subscriptionStartDate}
                  name="subscriptionStartDate"
                  onChange={handleChange}
                  label="Subscription Start Date"
                  format="M/d/yyyy"
                  validityStyles={settingError}
                  error={fields.subscriptionStartDate === "" && errors.subscriptionStartDate}
                  required={true}
                />
              </div>
            </>
          )}

          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              className="form-control"
              value={fields.activeStaffCount}
              onChange={handleChange}
              disabled={true}
              name="activeStaffCount"
              label="Active Staff"
              placeholder="Active Staff"
            />
          </div>

          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              className="form-control"
              value={fields.commissionPercentage}
              onChange={handleChange}
              name="commissionPercentage"
              label="Commission Percentage"
              placeholder="Commission Percentage"
              validityStyles={settingError}
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              className="form-control"
              value={fields.salesPerson}
              onChange={handleChange}
              name="salesPerson"
              label="Sales Person"
              placeholder="Sales Person"
            />
          </div>
          <div className="mb-3 col-lg-4 col-md-6 col-12">
            <InputKendoRct
              className="form-control"
              value={fields.referral}
              onChange={handleChange}
              name="referral"
              label="Referral"
              placeholder="Referral"
            />
          </div>

          {state && (
            <div className="mb-3 col-lg-4 col-md-6 col-12">
              <DropDownKendoRct
                filterable={true}
                label="Status"
                onChange={handleStatusChange}
                data={status}
                value={fields?.clinicStatus}
                textField="name"
                name="name"
                dataItemKey="id"
                suggest={true}
              />
            </div>
          )}

          <div className="k-chip-content">
            <Checkbox
              label={"Send Welcome Email"}
              value={sendWelcomeEmail}
              onChange={(e) => onCheckboxChange(e)}
            />
          </div>
          <div className="k-chip-content">
            <Checkbox
              label={"Is RCM"}
              name="isRCM"
              value={fields.isRCM}
              onChange={handleCheckboxChange}
            />
          </div>
        </div>
      </div>
      {loading == true && <Loader />}

      <div className="d-flex my-4 ">
        <button
          className="green-btn btn-primary btn-space mr-3"
          onClick={handleSubmit}
        >
          {state ? "Update" : "Add"}
        </button>

        <button className="btn grey-secondary btn-space" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </>
  );
}
export default AddClinic;
